import React from 'react';
import {
  Datagrid,
  FunctionField,
  List,
  Pagination,
  ReferenceInput,
  SelectInput,
  downloadCSV,
  TopToolbar,
  ExportButton,
} from 'react-admin';
import { createEmptyStateComponent } from '../../components/createEmptyStateComponent';
import { PageTitle } from '../../components/PageTitle';
import { IconButton, Tooltip, Box, Link as MuiLink } from '@mui/material';
import { downloadFileAuthenticated } from '../../utils/downloadFile';
import GetAppIcon from '@mui/icons-material/GetApp';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AssignmentIcon from '@mui/icons-material/Assignment';
import * as XLSX from 'xlsx';
import { Link } from 'react-router-dom';

const CompletedFormsEmptyState = createEmptyStateComponent('completed-forms');

const ListActions = () => (
  <TopToolbar>
    <ExportButton />
  </TopToolbar>
);

const handleDownloadPdf = async (formId: number) => {
  try {
    await downloadFileAuthenticated(`/completed-forms/${formId}/make-pdf`, `completed-form-${formId}.pdf`);
  } catch (error) {
    console.error('PDF download failed:', error);
    alert('Failed to download PDF. Please try again or contact support.');
  }
};

const completedBy = (record: {
  user?: {
    firstName?: string | null;
    lastName?: string | null;
    name?: string | null;
    email?: string | null;
  };
}) => {
  const u = record.user;
  if (!u) return '—';
  const name = [u.firstName, u.lastName].filter(Boolean).join(' ').trim();
  const single = u.name?.trim();
  return name || single || u.email || '—';
};

/** distill-hq-server exposes `formName` (nested `form` may be stripped); ext-server returns `form`. */
const formTypeLabel = (record: { form?: { name?: string }; formName?: string | null }) =>
  record.form?.name || record.formName || '—';

const contextOrService = (record: {
  customerService?: { name?: string; customer?: { displayName?: string; company?: string } };
  article?: { title?: string } | null;
  equipment?: { name?: string } | null;
}) => {
  const cs = record.customerService;
  if (cs) return cs.name || cs.customer?.displayName || cs.customer?.company || '—';
  if (record.article?.title) return record.article.title;
  if (record.equipment?.name) return record.equipment.name;
  return '—';
};

export const CompletedFormsList = () => {
  const exporter = React.useCallback((records: any[]) => {
    if (!Array.isArray(records) || records.length === 0) {
      downloadCSV('', 'completed-forms.csv');
      return;
    }

    const baseHeaders = ['Completed At', 'Completed By', 'Form Type', 'Context'];

    const formFieldKeys = new Set<string>();
    records.forEach((record) => {
      if (record.formValues && typeof record.formValues === 'object') {
        Object.keys(record.formValues).forEach((key) => {
          formFieldKeys.add(`formValues.${key}`);
        });
      }
    });
    const sortedFormFieldKeys = Array.from(formFieldKeys).sort();
    const headers = [...baseHeaders, ...sortedFormFieldKeys];

    const rows = records.map((record) => {
      const completedAt = record.completedAt ? new Date(record.completedAt).toLocaleString() : '';
      const formType = formTypeLabel(record);
      const formFieldData = sortedFormFieldKeys.map((key) => {
        const fieldName = key.replace('formValues.', '');
        const value = record.formValues?.[fieldName];
        if (value === null || value === undefined) return '';
        if (typeof value === 'boolean') return value ? 'Yes' : 'No';
        if (typeof value === 'object') return JSON.stringify(value);
        return String(value);
      });
      return [completedAt, completedBy(record), formType, contextOrService(record), ...formFieldData];
    });

    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const csv = XLSX.utils.sheet_to_csv(ws);
    downloadCSV(csv, 'completed-forms.csv');
  }, []);

  const filters = React.useMemo(
    () => [
      <ReferenceInput
        key="userId"
        source="userId"
        reference="users"
        label="User"
        alwaysOn
        sort={{ field: 'firstName', order: 'ASC' }}
      >
        <SelectInput
          optionText={(r: { firstName?: string; lastName?: string; email?: string }) =>
            [r.firstName, r.lastName].filter(Boolean).join(' ').trim() || r.email || ''
          }
          emptyText="All users"
          sx={{ minWidth: 220, '& .MuiOutlinedInput-root': { minWidth: 220, height: '56px' } }}
        />
      </ReferenceInput>,
      <ReferenceInput
        key="formId"
        source="formId"
        reference="forms"
        label="Form type"
        alwaysOn
        sort={{ field: 'name', order: 'ASC' }}
      >
        <SelectInput
          optionText="name"
          emptyText="All form types"
          sx={{ minWidth: 220, '& .MuiOutlinedInput-root': { minWidth: 220, height: '56px' } }}
        />
      </ReferenceInput>,
    ],
    [],
  );

  return (
    <Box>
      <PageTitle title="Completed Forms" subtitle="Completed form submissions" icon={<AssignmentIcon />} />
      <List
        resource="completed-forms/reports"
        perPage={25}
        sort={{ field: 'completedAt', order: 'DESC' }}
        empty={<CompletedFormsEmptyState />}
        pagination={<Pagination rowsPerPageOptions={[10, 25, 50, 100]} />}
        filters={filters}
        storeKey="completed-forms.list"
        actions={<ListActions />}
        exporter={exporter}
      >
        <Datagrid bulkActionButtons={false} rowClick={(id) => `/completed-forms/${id}/show`}>
          <FunctionField
            label="Completed"
            render={(record: { completedAt: string }) =>
              record.completedAt ? new Date(record.completedAt).toLocaleString() : '—'
            }
            source="completedAt"
          />
          <FunctionField label="Completed by" render={(record: any) => completedBy(record)} />
          <FunctionField label="Form type" render={(record: any) => formTypeLabel(record)} />
          <FunctionField label="Context" render={(record: any) => contextOrService(record)} />
          <FunctionField
            label="Details"
            render={(record: { id: number }) => (
              <MuiLink
                component={Link}
                to={`/completed-forms/${record.id}/show`}
                underline="hover"
                onClick={(e) => e.stopPropagation()}
                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
              >
                View <OpenInNewIcon sx={{ fontSize: 16 }} />
              </MuiLink>
            )}
          />
          <FunctionField
            label="PDF"
            render={(record: { id: number }) => (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Tooltip title="Download PDF">
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDownloadPdf(record.id);
                    }}
                  >
                    <GetAppIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          />
        </Datagrid>
      </List>
    </Box>
  );
};
